/**
 * TimeTrex Taller - TypeScript Models
 */

// ============== ROLES Y STATUS ==============

export enum Role {
  ADMIN = 1,
  JEFE_TALLER = 2,
  OPERARIO = 3,
  RECEPCIONISTA = 4
}

export enum UserStatus {
  ACTIVE = 10,
  INACTIVE = 20,
  DELETED = 30
}

export enum PunchType {
  IN = 10,
  OUT = 20
}

export enum PunchStatus {
  NORMAL = 10,
  LATE = 20
}

export enum RequestType {
  VACATION = 10,
  SICK = 20,
  PERSONAL = 30,
  BEREAVEMENT = 40
}

export enum RequestStatus {
  PENDING = 10,
  APPROVED = 20,
  DENIED = 30,
  CANCELLED = 40
}

export enum JobPriority {
  NORMAL = 10,
  URGENT = 20,
  CRITICAL = 30
}

export enum JobEstado {
  RECIBIDO = 10,
  DIAGNOSTICO = 20,
  COTIZACION = 30,
  EN_PROCESO = 40,
  ESPERA = 50,
  TERMINADO = 60,
  ENTREGADO = 70,
  CANCELADO = 80
}

export enum OperarioEstado {
  LIBRE = 10,
  ASIGNADO = 20,
  TRABAJANDO = 30,
  PAUSA = 40,
  ALMUERZO = 50,
  ESPERA = 60,
  FUERA = 70
}

export enum StepStatus {
  PENDIENTE = 10,
  EN_CURSO = 20,
  PAUSADA = 30,
  COMPLETADA = 40
}

export enum ActivityStatus {
  INICIO = 10,
  PAUSA = 20,
  REANUDACION = 30,
  FIN = 40,
  NOTA = 50
}

// ============== INTERFACES ==============

export interface User {
  id: string;
  company_id: string;
  branch_id: string;
  department_id: string;
  role_id: Role;
  user_name: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  employee_number: string;
  hire_date: string;
  status_id: UserStatus;
  created_date: string;
}

export interface Session {
  session_id: string;
  user: User;
}

export interface Punch {
  id: string;
  user_id: string;
  punch_control_id: string;
  type_id: PunchType;
  status_id: PunchStatus;
  time_stamp: string;
  location: string;
  note: string;
  created_date: string;
}

export interface Request {
  id: string;
  user_id: string;
  type_id: RequestType;
  status_id: RequestStatus;
  start_date: string;
  end_date: string;
  days_count: number;
  reason: string;
  approver_id: string | null;
  approver_note: string;
  created_date: string;
}

export interface Vehicle {
  id: string;
  placa: string;
  marca: string;
  modelo: string;
  anio: number;
  color: string;
  kilometraje: number;
  cliente_nombre: string;
  cliente_telefono: string;
  cliente_email: string;
  cliente_dni: string;
  observations: string;
  created_date: string;
}

export interface Job {
  id: string;
  vehicle_id: string;
  vehicle?: Vehicle;
  cliente_nombre: string;
  cliente_telefono: string;
  fecha_ingreso: string;
  fecha_entrega: string | null;
  priority_id: JobPriority;
  estado_id: JobEstado;
  reception_user_id: string;
  assigned_to: string;
  created_date: string;
  steps?: JobStep[];
  activities?: JobActivity[];
  assignedUsers?: User[];
}

export interface Etapa {
  id: string;
  nombre: string;
  orden: number;
  duracion_estimada: number;
  color: string;
  is_active: boolean;
}

export interface JobStep {
  id: string;
  job_id: string;
  etapa_id: string;
  etapa?: Etapa;
  status_id: StepStatus;
  started_at: string | null;
  completed_at: string | null;
  technician_id: string | null;
  technician?: User;
}

export interface JobActivity {
  id: string;
  job_id: string;
  step_id: string;
  operario_id: string;
  operario?: User;
  status_id: ActivityStatus;
  inicio: string;
  fin: string | null;
  duracion_minutos: number | null;
  nota: string;
  created_date: string;
}

export interface OperarioStatus {
  id: string;
  user_id: string;
  user?: User;
  status_id: OperarioEstado;
  job_id: string | null;
  job?: Job | null;
  stage_id: string | null;
  inicio_status: string;
  ubicacion: string;
  nota: string;
}

export interface Service {
  id: string;
  nombre: string;
  descripcion: string;
  duracion_estimada: number;
  precio_base: number;
  is_active: boolean;
}

// ============== API RESPONSES ==============

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  timestamp?: string;
}

export interface AttendanceStatus {
  is_clocked_in: boolean;
  last_punch: string | null;
  operario_status: OperarioStatus | null;
}

export interface EfficiencyReport {
  period: { start: string; end: string };
  total_jobs: number;
  completed_jobs: number;
  efficiency_rate: string;
  total_hours: string;
  avg_hours_per_job: string;
  jobs: Job[];
}

export interface OperarioReport {
  user: User;
  period: { start: string; end: string };
  total_minutes: number;
  total_hours: string;
  stages_completed: number;
  by_day: Record<string, number>;
  activities: JobActivity[];
}

// ============== HELPER FUNCTIONS ==============

export const getRoleName = (roleId: Role): string => {
  const names = {
    [Role.ADMIN]: 'Administrador',
    [Role.JEFE_TALLER]: 'Jefe de Taller',
    [Role.OPERARIO]: 'Operario',
    [Role.RECEPCIONISTA]: 'Recepcionista'
  };
  return names[roleId] || 'Desconocido';
};

export const getJobEstadoName = (estadoId: JobEstado): string => {
  const names = {
    [JobEstado.RECIBIDO]: 'Recibido',
    [JobEstado.DIAGNOSTICO]: 'Diagnóstico',
    [JobEstado.COTIZACION]: 'Cotización',
    [JobEstado.EN_PROCESO]: 'En Proceso',
    [JobEstado.ESPERA]: 'Espera',
    [JobEstado.TERMINADO]: 'Terminado',
    [JobEstado.ENTREGADO]: 'Entregado',
    [JobEstado.CANCELADO]: 'Cancelado'
  };
  return names[estadoId] || 'Desconocido';
};

export const getJobEstadoColor = (estadoId: JobEstado): string => {
  const colors = {
    [JobEstado.RECIBIDO]: 'bg-green-500',
    [JobEstado.DIAGNOSTICO]: 'bg-yellow-500',
    [JobEstado.COTIZACION]: 'bg-orange-500',
    [JobEstado.EN_PROCESO]: 'bg-blue-500',
    [JobEstado.ESPERA]: 'bg-purple-500',
    [JobEstado.TERMINADO]: 'bg-green-600',
    [JobEstado.ENTREGADO]: 'bg-gray-500',
    [JobEstado.CANCELADO]: 'bg-red-500'
  };
  return colors[estadoId] || 'bg-gray-500';
};

export const getOperarioEstadoName = (estadoId: OperarioEstado): string => {
  const names = {
    [OperarioEstado.LIBRE]: 'Libre',
    [OperarioEstado.ASIGNADO]: 'Asignado',
    [OperarioEstado.TRABAJANDO]: 'Trabajando',
    [OperarioEstado.PAUSA]: 'En Pausa',
    [OperarioEstado.ALMUERZO]: 'Almuerzo',
    [OperarioEstado.ESPERA]: 'Espera',
    [OperarioEstado.FUERA]: 'Fuera'
  };
  return names[estadoId] || 'Desconocido';
};

export const getOperarioEstadoColor = (estadoId: OperarioEstado): string => {
  const colors = {
    [OperarioEstado.LIBRE]: 'bg-green-500',
    [OperarioEstado.ASIGNADO]: 'bg-yellow-500',
    [OperarioEstado.TRABAJANDO]: 'bg-blue-500',
    [OperarioEstado.PAUSA]: 'bg-orange-500',
    [OperarioEstado.ALMUERZO]: 'bg-gray-500',
    [OperarioEstado.ESPERA]: 'bg-purple-500',
    [OperarioEstado.FUERA]: 'bg-red-500'
  };
  return colors[estadoId] || 'bg-gray-500';
};

export const getPriorityName = (priorityId: JobPriority): string => {
  const names = {
    [JobPriority.NORMAL]: 'Normal',
    [JobPriority.URGENT]: 'Urgente',
    [JobPriority.CRITICAL]: 'Crítica'
  };
  return names[priorityId] || 'Normal';
};

export const getRequestTypeName = (typeId: RequestType): string => {
  const names = {
    [RequestType.VACATION]: 'Vacaciones',
    [RequestType.SICK]: 'Enfermedad',
    [RequestType.PERSONAL]: 'Personal',
    [RequestType.BEREAVEMENT]: 'Duelo'
  };
  return names[typeId] || 'Desconocido';
};

export const getStepStatusName = (statusId: StepStatus): string => {
  const names = {
    [StepStatus.PENDIENTE]: 'Pendiente',
    [StepStatus.EN_CURSO]: 'En Curso',
    [StepStatus.PAUSADA]: 'Pausada',
    [StepStatus.COMPLETADA]: 'Completada'
  };
  return names[statusId] || 'Desconocido';
};
